import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { MoreVertical } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { Badge, type BadgeVariant } from '../../components/ui/badge';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { SelectField } from '../../components/ui/SelectField';
import { PageHeader } from '../../components/layout/PageHeader';
import { useClickOutside } from '../../lib/useClickOutside';
import { ApiError } from '../../lib/api';
import { useCurrentUser } from '../auth';
import { listAccounts } from '../accounts/api';
import {
  DEFAULT_SURVEY_SORT,
  SURVEY_STATUS_FILTER_OPTIONS,
  filterSurveysByStatus,
  nextSort,
  sortSurveys,
  type SurveySort,
  type SurveySortKey,
  type SurveyStatusFilter,
} from '../../lib/surveyListView';
import type { Account } from '../../types/account';
import type { AudienceScope } from '../../types/broadcast';
import type { Survey } from '../../types/survey';
import { deleteSurvey, duplicateSurvey, listSurveys } from './api';

// Phase 6B B8 + F5 — surveys list. Session-persisted status filter + title
// search + sortable columns (default: most-recently-sent first, drafts last),
// broadcast-list visual style. Per-row actions: Edit (drafts) / View (sent) /
// Duplicate / Delete. Admin-only link into the deleted-surveys recovery view (§9).

const STATUS_LABEL: Record<Survey['status'], string> = {
  draft: 'Draft',
  sending: 'Sending',
  open: 'Open',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

const STATUS_VARIANT: Record<Survey['status'], BadgeVariant> = {
  draft: 'info',
  sending: 'warning',
  open: 'success',
  closed: 'neutral',
  cancelled: 'danger',
};

// Session-persisted UI state (survives in-tab navigation). Default status 'All'.
const STATUS_KEY = 'surveys.statusFilter';
const SORT_KEY = 'surveys.sort';

function loadStatus(): SurveyStatusFilter {
  const v = sessionStorage.getItem(STATUS_KEY);
  return SURVEY_STATUS_FILTER_OPTIONS.some((o) => o.value === v) ? (v as SurveyStatusFilter) : 'all';
}
function loadSort(): SurveySort {
  try {
    const raw = sessionStorage.getItem(SORT_KEY);
    if (raw) {
      const p = JSON.parse(raw) as SurveySort;
      if (p && typeof p.key === 'string' && (p.dir === 'asc' || p.dir === 'desc')) return p;
    }
  } catch {
    /* ignore malformed */
  }
  return DEFAULT_SURVEY_SORT;
}

function brandLabel(scope: AudienceScope, accounts: Account[]): string {
  if (scope.brandSelection === 'all') return 'All brands';
  const ids = scope.brandSelection.brandAccountIds;
  if (ids.length === 0) return '—';
  const names = ids
    .map((id) => accounts.find((a) => a.id === id)?.name)
    .filter((n): n is string => Boolean(n));
  return names.length > 0 ? names.join(', ') : '—';
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function SurveysPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useCurrentUser();
  const surveys = useQuery({ queryKey: ['surveys'], queryFn: listSurveys });
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });

  const [status, setStatusState] = useState<SurveyStatusFilter>(loadStatus);
  const [sort, setSortState] = useState<SurveySort>(loadSort);
  const [search, setSearch] = useState('');
  const [pendingDelete, setPendingDelete] = useState<Survey | null>(null);

  const setStatus = (v: SurveyStatusFilter) => {
    setStatusState(v);
    sessionStorage.setItem(STATUS_KEY, v);
  };
  const onSort = (key: SurveySortKey) => {
    setSortState((cur) => {
      const next = nextSort(cur, key);
      sessionStorage.setItem(SORT_KEY, JSON.stringify(next));
      return next;
    });
  };

  const acc = accounts.data ?? [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const byStatus = filterSurveysByStatus(surveys.data ?? [], status);
    const bySearch =
      q === '' ? byStatus : byStatus.filter((s) => s.title.toLowerCase().includes(q));
    return sortSurveys(bySearch, sort, (s) => brandLabel(s.audienceScope, acc));
  }, [surveys.data, status, search, sort, acc]);

  const duplicate = useMutation({
    mutationFn: (id: string) => duplicateSurvey(id),
    onSuccess: (s) => {
      qc.invalidateQueries({ queryKey: ['surveys'] });
      toast.success('Survey duplicated.');
      navigate(`/surveys/${s.id}/edit`);
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not duplicate.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteSurvey(id),
    onSuccess: ({ kind }) => {
      qc.invalidateQueries({ queryKey: ['surveys'] });
      setPendingDelete(null);
      toast.success(kind === 'hard' ? 'Draft deleted.' : 'Survey deleted.');
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not delete.'),
  });

  return (
    <div>
      <PageHeader
        title="Surveys"
        description="Create and send surveys to your contacts."
        actions={
          <>
            {me.role === 'admin' && (
              <Button variant="outline" asChild>
                <Link to="/surveys/deleted">Deleted</Link>
              </Button>
            )}
            <Button asChild>
              <Link to="/surveys/new">New survey</Link>
            </Button>
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SelectField
          value={status}
          onValueChange={(v) => setStatus(v as SurveyStatusFilter)}
          size="sm"
          className="w-40"
          options={SURVEY_STATUS_FILTER_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
        />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by title…"
          className="w-64 rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
        />
      </div>

      {surveys.isLoading && <CenteredSpinner />}
      {surveys.error && <p className="text-sm text-red-600">Couldn't load surveys.</p>}

      {surveys.data && filtered.length === 0 && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center">
          <p className="text-sm font-medium text-slate-700">
            {surveys.data.length === 0 ? 'No surveys yet' : 'No surveys match your filters'}
          </p>
          {surveys.data.length === 0 && (
            <div className="mt-4">
              <Button asChild>
                <Link to="/surveys/new">New survey</Link>
              </Button>
            </div>
          )}
        </div>
      )}

      {filtered.length > 0 && (
        <div className="overflow-visible rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <SortHeader label="Title" sortKey="title" sort={sort} onSort={onSort} />
                <SortHeader label="Brand" sortKey="brand" sort={sort} onSort={onSort} />
                <th className="px-4 py-2 font-medium">Channels</th>
                <SortHeader label="Status" sortKey="status" sort={sort} onSort={onSort} />
                <SortHeader label="Sent" sortKey="sentAt" sort={sort} onSort={onSort} />
                <SortHeader label="Recipients" sortKey="recipients" sort={sort} onSort={onSort} />
                <SortHeader label="Responses" sortKey="responses" sort={sort} onSort={onSort} />
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((s) => (
                <tr
                  key={s.id}
                  onClick={() => navigate(s.status === 'draft' ? `/surveys/${s.id}/edit` : `/surveys/${s.id}`)}
                  className="cursor-pointer hover:bg-slate-50"
                >
                  <td className="px-4 py-3 font-medium text-slate-800">{s.title || 'Untitled survey'}</td>
                  <td className="px-4 py-3 text-slate-600">{brandLabel(s.audienceScope, acc)}</td>
                  <td className="px-4 py-3 text-slate-600">{s.channels.length > 0 ? s.channels.join(' + ') : '—'}</td>
                  <td className="px-4 py-3">
                    <Badge variant={STATUS_VARIANT[s.status]}>{STATUS_LABEL[s.status]}</Badge>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{formatDate(s.sentAt)}</td>
                  <td className="px-4 py-3 text-slate-600">{s.totalRecipients}</td>
                  <td className="px-4 py-3 text-slate-600">{s.submittedCount}</td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <RowMenu
                      survey={s}
                      onView={() => navigate(`/surveys/${s.id}`)}
                      onEdit={() => navigate(`/surveys/${s.id}/edit`)}
                      onDuplicate={() => duplicate.mutate(s.id)}
                      onDelete={() => setPendingDelete(s)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pendingDelete && (
        <ConfirmDelete
          survey={pendingDelete}
          busy={remove.isPending}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => remove.mutate(pendingDelete.id)}
        />
      )}
    </div>
  );
}

// Clickable sort header — shows ▲/▼ on the active column, ↕ otherwise (mirrors
// the broadcast list).
function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
}: {
  label: string;
  sortKey: SurveySortKey;
  sort: SurveySort;
  onSort: (key: SurveySortKey) => void;
}) {
  const active = sort.key === sortKey;
  return (
    <th
      className="px-4 py-2 font-medium"
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-slate-700"
      >
        <span>{label}</span>
        <span aria-hidden className="text-[10px] text-slate-400">
          {active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}

function RowMenu({
  survey,
  onView,
  onEdit,
  onDuplicate,
  onDelete,
}: {
  survey: Survey;
  onView: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside([ref], () => setOpen(false), open);
  const item = (label: string, onClick: () => void, danger = false) => (
    <button
      type="button"
      onClick={() => {
        setOpen(false);
        onClick();
      }}
      className={'block w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50 ' + (danger ? 'text-red-600' : 'text-slate-700')}
    >
      {label}
    </button>
  );
  return (
    <div ref={ref} className="relative inline-block">
      <button type="button" onClick={() => setOpen((o) => !o)} className="rounded p-1 text-slate-400 hover:bg-slate-100" aria-label="Survey actions">
        <MoreVertical className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute right-0 z-10 mt-1 w-32 overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg">
          {survey.status === 'draft' ? item('Edit', onEdit) : item('View', onView)}
          {item('Duplicate', onDuplicate)}
          {item('Delete', onDelete, true)}
        </div>
      )}
    </div>
  );
}

function ConfirmDelete({
  survey,
  busy,
  onCancel,
  onConfirm,
}: {
  survey: Survey;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const isDraft = survey.status === 'draft';
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30 p-4" onClick={onCancel}>
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold text-slate-800">Delete survey?</h3>
        <p className="mt-2 text-sm text-slate-600">
          {isDraft
            ? `"${survey.title || 'Untitled survey'}" is a draft and will be permanently deleted.`
            : `"${survey.title}" has been sent. It will be hidden, but its responses are preserved and an admin can restore it.`}
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={busy}>
            {busy ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      </div>
    </div>
  );
}
